import { hasCompleteProfile } from '../domainTypes/auth';
import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';

export const authGuard: CanActivateFn = (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isAuthenticated()) {
        return router.createUrlTree(['/login'], { queryParams: { next: state.url } });
    }

    // Verified user with no workspace_id is mid-onboarding - route them back
    // into the wizard until they finish. True for personal now too: a
    // personal signup with no existing company-domain match defers workspace
    // creation to the wizard's finish, exactly like corporate.
    const user = auth.state().user;
    if (user && user.workspace_id == null) {
        return router.createUrlTree(['/onboarding/setup']);
    }

    // Accounts created before a name became required at signup have blank names, and
    // with no IdP feed nothing fills them in the background - this gate is the only
    // thing that will (CIQ-1234). The target page auto-forwards once a first name is
    // on file, so it self-disables as the backlog drains.
    //
    // Gated on the first name alone: a surname is optional, so requiring one here
    // would trap mononymous users on a page they cannot satisfy.
    //
    // Restricted to active accounts on purpose: guests are nameless by design, and
    // pending/archived users have their own flows to finish first.
    //
    // complete_profile_gate_enabled is the rollout switch. It is the only flagged
    // part of CIQ-1234 - the redirect is a hard block on reaching the app, and most
    // existing accounts have no name on file, so it is enabled per workspace rather
    // than everywhere at once. Absent or false means no gate, so a cached session or
    // an LD outage can never trap someone here.
    //
    // email_is_verified is required too, because the save this page depends on is
    // refused for an unverified user: get_current_user_for_onboarding raises 403
    // "Email not verified". That state is reachable - activate_pending_user sets
    // status='active' in the same UPDATE that issues a verification code - and this
    // page has no exit but Sign out, so gating an unverified user parks them somewhere
    // they cannot complete. They finish verification first, then get gated.
    //
    // A viewer is deliberately NOT excluded: PUT /users/settings is allowlisted for
    // read-only members (workspace_access.is_viewer_allowlisted_write) precisely so
    // this page works for them, since it is a self-write on their own user row.
    //
    // Known gap, deliberately not closed here: this guard is not reactive, and
    // restoreSessionFromCookie sets status 'authenticated' before /users/me lands. In
    // that window `user` is null, so the condition falls through and the gate is
    // skipped for the rest of that session. It is a best-effort client gate, not a
    // server-enforced one - nothing sensitive is behind it. Tracked separately.
    if (user && user.complete_profile_gate_enabled && user.status === 'active' && user.email_is_verified && !hasCompleteProfile(user)) {
        return router.createUrlTree(['/complete-your-profile'], { queryParams: { next: state.url } });
    }

    return true;
};
